import { LocationEditComponent } from './components/location/location-edit/location-edit.component';
import { LocationNewComponent } from './components/location/location-new/location-new.component';
import { LocationListComponent } from './components/location/location-list/location-list.component';
import { BranchEditComponent } from './components/branch/branch-edit/branch-edit.component';
import { BranchNewComponent } from './components/branch/branch-new/branch-new.component';
import { BranchListComponent } from './components/branch/branch-list/branch-list.component';
import { Injectable, NgModule } from '@angular/core';
import { Routes, RouterModule, DefaultUrlSerializer, UrlTree, UrlSerializer } from '@angular/router';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { MainComponent } from './main.component';
import { AuthGuard } from './services/auth-guard.service';
import { AuthService } from './services/auth.service';
import { Utilities } from './services/utilities';
import { MediaLibraryComponent } from './components/media-library/media-library.component';
import { MediaLibraryHttpResolverService } from './resolvers/media-library-http-resolver.service';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { UserNewComponent } from './components/user/user-new/user-new.component';
import { UserEditComponent } from './components/user/user-edit/user-edit.component';
import { BranchEditHttpResolverService, BranchListHttpResolverService } from './resolvers/branch-http-resolver.service';
import { LocationEditHttpResolverService, LocationListHttpResolverService, LocationNewHttpResolverService } from './resolvers/location-http-resolver.service';
import { SectionListComponent } from './components/section/section-list/section-list.component';
import { SectionNewComponent } from './components/section/section-new/section-new.component';
import { SectionEditComponent } from './components/section/section-edit/section-edit.component';
import { SectionListHttpResolverService, SectionEditHttpResolverService, SectionNewHttpResolverService } from './resolvers/section-http-resolver.service';
import { ProductListComponent } from './components/product/product-list/product-list.component';
import { ProductNewComponent } from './components/product/product-new/product-new.component';
import { ProductEditComponent } from './components/product/product-edit/product-edit.component';
import { ProductListHttpResolverService, ProductNewHttpResolverService, ProductEditHttpResolverService } from './resolvers/product-http-resolver.service';
import { HomePageComponent } from './components/home-page/home-page/home-page.component';
import { HomePageHttpResolverService } from './resolvers/home-page-http-resolver.service';
import {
    UserEditHttpResolverService,
    UserListHttpResolverService,
    UserNewHttpResolverService,
    UserPasswordEditHttpResolverService,
} from './resolvers/user-http-resolver.service';
import { UserPasswordEditComponent } from './components/user/user-password-edit/user-password-edit.component';
import { ProductAvailabilityComponent } from './components/management/product-availability/product-availability.component';
import { ProductAvailabiltyHttpResolverService } from './resolvers/management-http-resolver.service';
import { OrderListComponent } from './components/order/order-list/order-list.component';
import { OrdersListHttpResolverService } from './resolvers/order-http-resolver.service';
import { SurveyComponent } from './components/engagement/survey/survey.component';
import { ReviewComponent } from './components/engagement/review/review.component';
import { ContactComponent } from './components/engagement/contact/contact.component';
import { ContactHttpResolverService, ReviewHttpResolverService, SurveyHttpResolverService } from './resolvers/engagement-http-resolver.service';
import { EndOfDayReportComponent } from './components/reports/end-of-day-report/end-of-day-report.component';
import { IsAdminGuard } from './guards/is-admin.guard';
import { DashboardHttpResolverService } from './resolvers/dashboard-http-resolver.service';
import { EndOfDayReportHttpResolverService } from './resolvers/report-http-resolver.service';
import { DiscountPeriodComponent } from './components/offer/discount-period/discount-period.component';
import { VoucherListComponent } from './components/offer/voucher-code/voucher-list/voucher-list.component';
import { VoucherNewComponent } from './components/offer/voucher-code/voucher-new/voucher-new.component';
import { DiscountPeriodHttpResolverService, VoucherCodeHttpResolverService } from './resolvers/offer-http-resolver.service';
import { AccessOrdersList } from './guards/access-orders-list.guard';
import { ComplainEditComponent } from './components/complain/complain-edit/complain-edit.component';
import { ComplainNewComponent } from './components/complain/complain-new/complain-new.component';
import { ComplainListComponent } from './components/complain/complain-list/complain-list.component';
import { ComplainEditHttpResolverService, ComplainListHttpResolverService, ComplainNewHttpResolverService } from './resolvers/complain-http-resolver.service';
import { AccessComplainsList } from './guards/access-complains-list.guard';

@Injectable()
export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const possibleSeparators = /[?;#]/;
        const indexOfSeparator = url.search(possibleSeparators);
        let processedUrl: string;

        if (indexOfSeparator > -1) {
            const separator = url.charAt(indexOfSeparator);
            const urlParts = Utilities.splitInTwo(url, separator);
            urlParts.firstPart = urlParts.firstPart.toLowerCase();

            processedUrl = urlParts.firstPart + separator + urlParts.secondPart;
        } else {
            processedUrl = url.toLowerCase();
        }

        return super.parse(processedUrl);
    }
}

const routes: Routes = [
    {
        path: '',
        component: MainComponent,
        canActivate: [AuthGuard],
        children: [
            { path: '', component: HomeComponent, resolve: { data: DashboardHttpResolverService } },

            { path: 'user-list', component: UserListComponent, resolve: { data: UserListHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'user-list/user-new', component: UserNewComponent, resolve: { data: UserNewHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'user-list/user-edit/:id', component: UserEditComponent, resolve: { data: UserEditHttpResolverService }, canActivate: [IsAdminGuard] },
            {
                path: 'user-list/user-password-edit/:id',
                component: UserPasswordEditComponent,
                resolve: { data: UserPasswordEditHttpResolverService },
                canActivate: [IsAdminGuard],
            },

            { path: 'media-library', component: MediaLibraryComponent, resolve: { data: MediaLibraryHttpResolverService }, canActivate: [IsAdminGuard] },

            { path: 'branch-list', component: BranchListComponent, resolve: { data: BranchListHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'branch-list/branch-new', component: BranchNewComponent, canActivate: [IsAdminGuard] },
            {
                path: 'branch-list/branch-edit/:id',
                component: BranchEditComponent,
                resolve: { data: BranchEditHttpResolverService },
                canActivate: [IsAdminGuard],
            },

            { path: 'section-list', component: SectionListComponent, resolve: { data: SectionListHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'section-list/section-new', component: SectionNewComponent, resolve: { data: SectionNewHttpResolverService }, canActivate: [IsAdminGuard] },
            {
                path: 'section-list/section-edit/:id',
                component: SectionEditComponent,
                resolve: { data: SectionEditHttpResolverService },
                canActivate: [IsAdminGuard],
            },

            { path: 'product-list', component: ProductListComponent, resolve: { data: ProductListHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'product-list/product-new', component: ProductNewComponent, resolve: { data: ProductNewHttpResolverService }, canActivate: [IsAdminGuard] },
            {
                path: 'product-list/product-edit/:id',
                component: ProductEditComponent,
                resolve: { data: ProductEditHttpResolverService },
                canActivate: [IsAdminGuard],
            },

            { path: 'location-list', component: LocationListComponent, resolve: { data: LocationListHttpResolverService }, canActivate: [IsAdminGuard] },
            {
                path: 'location-list/location-new',
                component: LocationNewComponent,
                resolve: { data: LocationNewHttpResolverService },
                canActivate: [IsAdminGuard],
            },
            {
                path: 'location-list/location-edit/:id',
                component: LocationEditComponent,
                resolve: { data: LocationEditHttpResolverService },
                canActivate: [IsAdminGuard],
            },

            {
                path: 'management/complain-list',
                component: ComplainListComponent,
                resolve: { data: ComplainListHttpResolverService },
                canActivate: [AccessComplainsList],
            },
            {
                path: 'complain-list/complain-new',
                component: ComplainNewComponent,
                resolve: { data: ComplainNewHttpResolverService },
            },
            {
                path: 'complain-list/complain-edit/:id',
                component: ComplainEditComponent,
                resolve: { data: ComplainEditHttpResolverService },
            },

            { path: 'home-page', component: HomePageComponent, resolve: { data: HomePageHttpResolverService } },

            { path: 'management/product-availability', component: ProductAvailabilityComponent, resolve: { data: ProductAvailabiltyHttpResolverService } },

            {
                path: 'orders/orders-list',
                component: OrderListComponent,
                resolve: { data: OrdersListHttpResolverService },
                canActivate: [IsAdminGuard, AccessOrdersList],
            },

            { path: 'engagement/contact', component: ContactComponent, resolve: { data: ContactHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'engagement/review', component: ReviewComponent, resolve: { data: ReviewHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'engagement/survey', component: SurveyComponent, resolve: { data: SurveyHttpResolverService }, canActivate: [IsAdminGuard] },

            { path: 'report/end-of-day', component: EndOfDayReportComponent, resolve: { data: EndOfDayReportHttpResolverService } },

            {
                path: 'offer/discount-period',
                component: DiscountPeriodComponent,
                resolve: { data: DiscountPeriodHttpResolverService },
                canActivate: [IsAdminGuard],
            },
            { path: 'offer/discount-list', component: VoucherListComponent, resolve: { data: VoucherCodeHttpResolverService }, canActivate: [IsAdminGuard] },
            { path: 'offer/discount-new', component: VoucherNewComponent, canActivate: [IsAdminGuard] },
        ],
    },
    { path: 'login', component: LoginComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
    providers: [AuthService, AuthGuard, { provide: UrlSerializer, useClass: LowerCaseUrlSerializer }],
})
export class AppRoutingModule {}
